// Smart ordering for filter values: months / quarters / weekdays chronologically,
// numeric strings numerically, dates by time, everything else natural A→Z.

const MONTHS = ["january", "february", "march", "april", "may", "june", "july", "august", "september", "october", "november", "december"];
const MONTHS_FR = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];
const MONTHS_AR = ["يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو", "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر"];
const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

function monthIndex(tok) {
  const t = String(tok || "").trim().toLowerCase().replace(/\.$/, "");
  if (!t) return -1;
  if (/^\d{1,2}$/.test(t)) { const n = Number(t); return n >= 1 && n <= 12 ? n - 1 : -1; }
  let i = MONTHS.findIndex((m) => m === t || (t.length >= 3 && m.startsWith(t)));
  if (i < 0) i = MONTHS_FR.findIndex((m) => m === t || (t.length >= 3 && m.startsWith(t)));
  if (i < 0) i = MONTHS_AR.indexOf(t);
  return i;
}

// Parses "Jan", "January", "01", "2025-01", "Jan 2025", "January-2025", "2025/Jan", "Q1", "Q1 2025", "FY25 Q3"
function periodKey(v) {
  const s = String(v ?? "").trim();
  if (!s) return null;
  let m = s.match(/^(\d{4})[-/ .]?(\d{1,2})$/);                       // 2025-01
  if (m) { const mi = Number(m[2]) - 1; if (mi >= 0 && mi < 12) return Number(m[1]) * 12 + mi; }
  m = s.match(/^([A-Za-zÀ-ÿ\u0600-\u06FF.]+)[-/ ,']*(\d{2,4})?$/);          // Jan / Jan 2025 / Jan-25
  if (m) {
    const mi = monthIndex(m[1]);
    if (mi >= 0) { const y = m[2] ? Number(m[2].length === 2 ? "20" + m[2] : m[2]) : 0; return y * 12 + mi; }
  }
  m = s.match(/^(\d{2,4})[-/ ,]+([A-Za-zÀ-ÿ\u0600-\u06FF.]+)$/);            // 2025 Jan
  if (m) { const mi = monthIndex(m[2]); if (mi >= 0) return Number(m[1].length === 2 ? "20" + m[1] : m[1]) * 12 + mi; }
  m = s.match(/^(?:FY\s?(\d{2,4})\s*)?Q([1-4])(?:[-/ ,']*(?:FY)?\s?(\d{2,4}))?$/i);  // Q1 / Q1 2025 / FY25 Q3
  if (m) { const yRaw = m[1] || m[3]; const y = yRaw ? Number(yRaw.length === 2 ? "20" + yRaw : yRaw) : 0; return y * 12 + (Number(m[2]) - 1) * 3; }
  m = s.match(/^(\d{4})[-/ ]?Q([1-4])$/i);                              // 2025-Q1
  if (m) return Number(m[1]) * 12 + (Number(m[2]) - 1) * 3;
  const mi = monthIndex(s);
  if (mi >= 0) return mi;
  return null;
}

function dayKey(v) {
  const t = String(v ?? "").trim().toLowerCase();
  const i = DAYS.findIndex((d) => d === t || (t.length >= 3 && d.startsWith(t)));
  return i >= 0 ? i : null;
}

const isNumeric = (v) => v !== "" && v != null && Number.isFinite(Number(String(v).replace(/,/g, "")));
const dateKey = (v) => { const t = Date.parse(String(v)); return Number.isFinite(t) && /\d{4}/.test(String(v)) ? t : null; };

// Detect the dominant kind of the value list (≥ 80% must match) and return a sort key fn.
export function detectValueKind(values) {
  const vals = (values || []).filter((v) => v != null && v !== "");
  if (!vals.length) return "text";
  const n = vals.length;
  const share = (fn) => vals.filter((v) => fn(v) != null && fn(v) !== false).length / n;
  if (share(isNumeric) >= 0.8) return "number";
  if (share(periodKey) >= 0.8) return "period";
  if (share(dayKey) >= 0.8) return "weekday";
  if (share(dateKey) >= 0.8) return "date";
  return "text";
}

export function smartComparator(values, kindOverride = null) {
  const kind = kindOverride && kindOverride !== "auto" ? kindOverride : detectValueKind(values);
  const key = kind === "number" ? (x) => Number(String(x).replace(/,/g, ""))
    : kind === "period" ? (x) => periodKey(x) ?? Number.MAX_SAFE_INTEGER
    : kind === "weekday" ? (x) => dayKey(x) ?? 99
    : kind === "date" ? (x) => dateKey(x) ?? Number.MAX_SAFE_INTEGER
    : null;
  if (key) return (a, b) => key(a) - key(b) || String(a).localeCompare(String(b));
  return (a, b) => String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
}

export function smartSortValues(values, sort = "asc", kindOverride = null) {
  const v = [...(values || [])];
  if (sort === "none") return v;
  v.sort(smartComparator(v, kindOverride));
  if (sort === "desc") v.reverse();
  return v;
}

// "year_" -> "Year", "customer_type" -> "Customer Type", "salesAmount" -> "Sales Amount"
export function humanizeField(name) {
  const s = String(name || "").replace(/^[_\s]+|[_\s]+$/g, "");
  if (!s) return "";
  return s.replace(/([a-z0-9])([A-Z])/g, "$1 $2").replace(/[_\-.]+/g, " ").replace(/\s+/g, " ").trim()
    .split(" ").map((w) => (w.length <= 3 && w === w.toUpperCase() ? w : w.charAt(0).toUpperCase() + w.slice(1))).join(" ");
}
